import { useState, useMemo } from 'react';
import { useUI, useFile } from '../../context/AppContext.jsx';
import { usePlugins } from '../../hooks/usePlugins.js';

export default function TransformModal() {
  const { showTransformModal, setShowTransformModal } = useUI();
  const { currentWaypoints } = useFile();
  const { transforms, applyTransform } = usePlugins();

  const [selectedTransform, setSelectedTransform] = useState('');
  const [scope, setScope] = useState('all');
  const [options, setOptions] = useState({});

  // Get current transform
  const currentTransform = useMemo(() => {
    return transforms.find(t => t.id === selectedTransform);
  }, [transforms, selectedTransform]);

  // Reset options when transform changes
  const handleTransformChange = (transformId) => {
    setSelectedTransform(transformId);
    // Reset options to defaults
    setOptions({});
  };

  const handleApply = () => {
    if (!selectedTransform) return;

    const success = applyTransform(selectedTransform, options, scope);
    if (success) {
      setShowTransformModal(false);
    }
  };

  const handleClose = () => {
    setShowTransformModal(false);
    setSelectedTransform('');
    setOptions({});
    setScope('all');
  };

  if (!showTransformModal) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50">
      <div className="bg-bg-secondary rounded-lg p-6 max-w-lg w-full mx-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Apply Transform</h2>
          <button
            onClick={handleClose}
            className="text-text-muted hover:text-text-primary"
          >
            ✕
          </button>
        </div>

        <div className="space-y-4">
          {/* Transform Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Transform</label>
            <select
              value={selectedTransform}
              onChange={(e) => handleTransformChange(e.target.value)}
              className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
            >
              <option value="">Select a transform...</option>
              {transforms.map((transform) => (
                <option key={transform.id} value={transform.id}>
                  {transform.name}
                </option>
              ))}
            </select>
            {currentTransform && (
              <p className="text-sm text-text-muted mt-1">
                {currentTransform.description}
              </p>
            )}
          </div>

          {/* Scope Selection */}
          <div>
            <label className="block text-sm font-medium mb-1">Apply to</label>
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
            >
              <option value="all">All Waypoints ({currentWaypoints?.length || 0})</option>
              <option value="selected">Selected Waypoints</option>
            </select>
          </div>

          {/* Transform-specific options */}
          {selectedTransform === 'offset' && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">X (mm)</label>
                  <input
                    type="number"
                    value={options.offsetX || 0}
                    onChange={(e) => setOptions({ ...options, offsetX: parseFloat(e.target.value) / 1000 })}
                    className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Y (mm)</label>
                  <input
                    type="number"
                    value={options.offsetY || 0}
                    onChange={(e) => setOptions({ ...options, offsetY: parseFloat(e.target.value) / 1000 })}
                    className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Z (mm)</label>
                  <input
                    type="number"
                    value={options.offsetZ || 0}
                    onChange={(e) => setOptions({ ...options, offsetZ: parseFloat(e.target.value) / 1000 })}
                    className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                  />
                </div>
              </div>
            </div>
          )}

          {selectedTransform === 'scale' && (
            <div className="space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">Scale X</label>
                  <input
                    type="number"
                    step="0.1"
                    value={options.scaleX || 1}
                    onChange={(e) => setOptions({ ...options, scaleX: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scale Y</label>
                  <input
                    type="number"
                    step="0.1"
                    value={options.scaleY || 1}
                    onChange={(e) => setOptions({ ...options, scaleY: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Scale Z</label>
                  <input
                    type="number"
                    step="0.1"
                    value={options.scaleZ || 1}
                    onChange={(e) => setOptions({ ...options, scaleZ: parseFloat(e.target.value) })}
                    className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                  />
                </div>
              </div>
            </div>
          )}

          {selectedTransform === 'rotate' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Axis</label>
                <select
                  value={options.axis || 'z'}
                  onChange={(e) => setOptions({ ...options, axis: e.target.value })}
                  className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                >
                  <option value="x">X Axis</option>
                  <option value="y">Y Axis</option>
                  <option value="z">Z Axis</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Angle (degrees)</label>
                <input
                  type="number"
                  value={(options.angle || 0) * (180 / Math.PI)}
                  onChange={(e) => setOptions({ ...options, angle: parseFloat(e.target.value) * (Math.PI / 180) })}
                  className="w-full px-3 py-2 bg-bg-primary border border-border-primary rounded text-text-primary"
                />
              </div>
            </div>
          )}

          {selectedTransform === 'reverse' && (
            <div>
              <label className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  checked={options.renumber || false}
                  onChange={(e) => setOptions({ ...options, renumber: e.target.checked })}
                  className="rounded"
                />
                <span className="text-sm">Renumber waypoints after reversing</span>
              </label>
            </div>
          )}
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={handleClose}
            className="px-4 py-2 border border-border-primary rounded hover:bg-bg-primary"
          >
            Cancel
          </button>
          <button
            onClick={handleApply}
            disabled={!selectedTransform}
            className="px-4 py-2 bg-primary text-white rounded hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Apply Transform
          </button>
        </div>
      </div>
    </div>
  );
}
